import React, { useEffect, useRef, useState } from "react";
import { Editor } from "primereact/editor";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Fieldset } from "primereact/fieldset";
import { Dropdown } from "primereact/dropdown";
import blog from "./blog.json";
import { useAuth } from "../auth";

interface BlogPost {
  title: string;
  content: string;
  date: string;
  links?: string[];
  images?: string[];
  subject?: string;
  youtubeId?: string;
  status?: "published" | "draft";
  hidden?: boolean;
}

const BlogPostEntry: React.FC = () => {
  const { isLoggedIn, token, user } = useAuth();
  const [blogPost, setBlogPost] = useState<BlogPost>({
    title: "",
    content: "",
    date: "",
    links: [""],
    images: [""],
    subject: "",
    youtubeId: "",
    status: "published",
    hidden: false,
  });

  // Local calendar state for nicer UX
  const [dateValue, setDateValue] = useState<Date | null>(null);

  // AI image generation state
  const [genPrompt, setGenPrompt] = useState("");
  const [genLoading, setGenLoading] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [genPreviewUrl, setGenPreviewUrl] = useState<string | null>(null);
  const [sdApiKey, setSdApiKey] = useState<string>("");
  // Entry password stored locally (not sent with post)
  const [entryPassword, setEntryPassword] = useState<string>("");

  // Voice transcription state
  const [isRecording, setIsRecording] = useState(false);
  const [recorder, setRecorder] = useState<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const [transcribing, setTranscribing] = useState(false);
  const [transcribeError, setTranscribeError] = useState<string | null>(null);
  const [lastTranscript, setLastTranscript] = useState<string | null>(null);

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [subjects, setSubjects] = useState<string[]>([]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: keyof BlogPost) => {
    setBlogPost({ ...blogPost, [field]: e.target.value });
  };

  const handleDateChange = (e: any) => {
    if (e?.value instanceof Date) {
      setDateValue(e.value);
      setBlogPost({ ...blogPost, date: e.value.toISOString().split("T")[0] });
    }
  };

  const handleRichTextChange = (e: any) => {
    setBlogPost({ ...blogPost, content: e.htmlValue });
  };

  const addLinkField = () => {
    setBlogPost({ ...blogPost, links: [...(blogPost.links || []), ""] });
  };

  const addImageField = () => {
    setBlogPost({ ...blogPost, images: [...(blogPost.images || []), ""] });
  };

  const handleLinkChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const updatedLinks = blogPost.links ? [...blogPost.links] : [];
    updatedLinks[index] = e.target.value;
    setBlogPost({ ...blogPost, links: updatedLinks });
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const updatedImages = blogPost.images ? [...blogPost.images] : [];
    updatedImages[index] = e.target.value;
    setBlogPost({ ...blogPost, images: updatedImages });
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    setSubmitSuccess(null);
    // simple validation
    if (!blogPost.title.trim()) {
      setSubmitError("Please enter a title.");
      return;
    }
    if (!blogPost.content.trim()) {
      setSubmitError("Please add some content.");
      return;
    }
    if (!isLoggedIn && !entryPassword.trim()) {
      setSubmitError("Sign in (or enter admin password) to submit.");
      return;
    }
    // Use today's date if none chosen
    const dateStr = blogPost.date || new Date().toISOString().split("T")[0];

    const imagesOut = (blogPost.images || []).filter((u) => !!u?.trim());

    const payload = {
      // keep original keys and also provide compatibility fields
      title: blogPost.title.trim(),
      content: blogPost.content,
      date: dateStr,
      links: (blogPost.links || []).filter((u) => !!u?.trim()),
      // Newer posts expect `images` as a single string; send first image
      images: imagesOut.length > 0 ? imagesOut[0] : undefined,
      subject: (blogPost.subject || "").trim() || undefined,
      youtubeId: (blogPost.youtubeId || "").trim() || undefined,
      status: (blogPost.status || "published").toString().toLowerCase() === "draft" ? "draft" : "published",
      hidden: !!blogPost.hidden,
      Title: blogPost.title.trim(),
      Body: blogPost.content,
      // include password for API validation
      password: entryPassword,
    };

    setSubmitting(true);
    try {
      const res = await fetch("/api/blog", {
        method: "POST",
        headers: { "content-type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Failed with ${res.status}`);
      }
      setSubmitSuccess("Saved! Your blog post was submitted.");
      // reset form
      setBlogPost({ title: "", content: "", date: "", links: [""], images: [""], subject: "", youtubeId: "", status: "published", hidden: false });
      setDateValue(null);
      setGenPrompt("");
      setGenPreviewUrl(null);
    } catch (err: any) {
      setSubmitError(err?.message || "Failed to submit blog post");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    // Load saved Stability API key
    try {
      const saved = localStorage.getItem("stability_api_key");
      if (saved) setSdApiKey(saved);
    } catch {}

    // Load saved blog entry password
    try {
      const pw = localStorage.getItem("blog_entry_password");
      if (pw) setEntryPassword(pw);
    } catch {}

    // Build subject list from bundled posts
    try {
      const unique = Array.from(
        new Set(
          (Array.isArray(blog) ? blog : []).map((p: any) => (p?.subject || "").toString().trim()).filter((s: string) => s.length > 0)
        )
      ).sort((a, b) => a.localeCompare(b));
      setSubjects(unique);
    } catch {}

    // Also include subjects from latest KV posts (if any)
    (async () => {
      try {
        const res = await fetch("/api/get-posts?limit=100");
        if (!res.ok) return;
        const data = await res.json();
        const posts: any[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.posts)
          ? data.posts
          : [];
        const kvSubjects = Array.from(
          new Set(
            posts
              .map((p) => (p?.subject || "").toString().trim())
              .filter((s: string) => s.length > 0)
          )
        );
        if (kvSubjects.length) {
          setSubjects((prev) =>
            Array.from(new Set([...(prev || []), ...kvSubjects])).sort((a, b) => a.localeCompare(b))
          );
        }
      } catch {
        // ignore network errors
      }
    })();

    // Cleanup created object URLs when component unmounts
    return () => {
      if (genPreviewUrl) URL.revokeObjectURL(genPreviewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateImage = async () => {
    setGenError(null);
    if (!genPrompt.trim()) {
      setGenError("Please enter a prompt.");
      return;
    }
    if (!sdApiKey.trim()) {
      setGenError("Please enter your Stability API key.");
      return;
    }
    setGenLoading(true);
    try {
      // Build multipart form as per Stability API
      const form = new FormData();
      form.append("prompt", genPrompt.trim());
      form.append("output_format", "jpeg");
      form.append("model", "sd3.5-flash");
      form.append("aspect_ratio", "21:9");

      const res = await fetch("https://api.stability.ai/v2beta/stable-image/generate/sd3", {
        method: "POST",
        headers: {
          authorization: `Bearer ${sdApiKey.trim()}`,
          accept: "image/*",
          // NOTE: do not set Content-Type when using FormData
        },
        body: form,
      });

      if (!res.ok) {
        const ct = res.headers.get("content-type") || "";
        if (ct.includes("application/json")) {
          const j = await res.json();
          throw new Error(j?.error || j?.message || `Request failed: ${res.status}`);
        }
        const t = await res.text();
        throw new Error(t || `Request failed: ${res.status}`);
      }

      // Response is an image; convert to data URL for preview + storage
      const blob = await res.blob();
      const reader = new FileReader();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        reader.onloadend = () => resolve(String(reader.result));
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      setGenPreviewUrl(dataUrl);
      const updated = [...(blogPost.images || [])];
      updated.push(dataUrl);
      setBlogPost({ ...blogPost, images: updated });
    } catch (err: any) {
      setGenError(err?.message || "Failed to generate image");
    } finally {
      setGenLoading(false);
    }
  };

  // Helpers for voice transcription
  const getSupportedMime = (): string => {
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/mp4",
      "audio/mpeg",
      "audio/wav",
    ];
    for (const t of candidates) {
      // @ts-ignore - MediaRecorder may not exist in type lib
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t)) return t;
    }
    return "audio/webm";
  };

  const startRecording = async () => {
    setTranscribeError(null);
    setLastTranscript(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("This browser does not support audio recording.");
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedMime();
      // @ts-ignore
      const mr: MediaRecorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      mr.ondataavailable = (e: any) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };
      mr.start();
      setRecorder(mr);
      setIsRecording(true);
    } catch (err: any) {
      setTranscribeError(err?.message || "Failed to start recording");
    }
  };

  const stopAndTranscribe = async () => {
    setTranscribeError(null);
    try {
      if (!recorder) return;
      const r: MediaRecorder = recorder;
      const mimeType = (r as any).mimeType || "audio/webm";
      const stopPromise = new Promise<void>((resolve) => {
        const handler = () => resolve();
        r.addEventListener("stop", handler, { once: true } as any);
      });
      r.stop();
      setIsRecording(false);
      await stopPromise;

      // Compose audio blob
      const parts = chunksRef.current && chunksRef.current.length ? chunksRef.current : [];
      const blob = new Blob(parts, { type: mimeType });
      if (!blob || blob.size === 0) {
        throw new Error("No audio captured. Please try recording again.");
      }
      setTranscribing(true);
      const res = await fetch("/api/transcribe", {
        method: "POST",
        headers: { "content-type": blob.type || "application/octet-stream" },
        body: blob,
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `Transcribe failed (${res.status})`);
      }
      const out = await res.json();
      const text: string = (out?.text || out?.response?.text || "").toString();
      if (!text) throw new Error("No transcript returned.");
      setLastTranscript(text);
      // Append transcript as a paragraph to the rich-text editor
      const escapeHtml = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const htmlPara = `<p>${escapeHtml(text).replace(/\n{2,}/g, "</p><p>").replace(/\n/g, "<br/>")}</p>`;
      setBlogPost((prev) => ({ ...prev, content: (prev.content || "") + htmlPara }));
    } catch (err: any) {
      setTranscribeError(err?.message || "Failed to transcribe");
    } finally {
      setTranscribing(false);
      // Release media stream
      try {
        const stream: MediaStream | undefined = (recorder as any)?.stream;
        stream?.getTracks?.().forEach((t) => t.stop());
      } catch {}
      setRecorder(null);
      chunksRef.current = [];
    }
  };

  return (
    <div className="p-fluid p-grid admin-entry" style={{ textAlign: "left" }}>
      <div className="p-col-12">
        {!isLoggedIn && (
          <div className="p-field">
            <label htmlFor="entryPassword" className="field-label">
              Password
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8 }}>
              <InputText
                id="entryPassword"
                value={entryPassword}
                onChange={(e) => {
                  setEntryPassword(e.target.value);
                  try {
                    localStorage.setItem("blog_entry_password", e.target.value);
                  } catch {}
                }}
                placeholder="Enter password or sign in"
                type="password"
              />
              <Button
                label="Clear"
                className="p-button-text"
                onClick={() => {
                  setEntryPassword("");
                  try {
                    localStorage.removeItem("blog_entry_password");
                  } catch {}
                }}
              />
            </div>
          </div>
        )}
        {isLoggedIn && (
          <div className="p-field" aria-live="polite"><strong>Signed in as {user?.email || user?.name}</strong></div>
        )}
        <div className="p-field">
          <label htmlFor="title" className="field-label">
            Title
          </label>
          <InputText id="title" value={blogPost.title} onChange={(e) => handleInputChange(e, "title")} placeholder="Enter the blog title" />
        </div>

        <Fieldset legend="Status & Visibility">
          <div className="p-field" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input
              id="statusDraft"
              type="checkbox"
              checked={(blogPost.status || "published") === "draft"}
              onChange={(e) => setBlogPost({ ...blogPost, status: e.target.checked ? "draft" : "published" })}
            />
            <label htmlFor="statusDraft" className="field-label" style={{ margin: 0 }}>Save as Draft</label>
          </div>
          <div className="p-field" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input
              id="hiddenToggle"
              type="checkbox"
              checked={!!blogPost.hidden}
              onChange={(e) => setBlogPost({ ...blogPost, hidden: e.target.checked })}
            />
            <label htmlFor="hiddenToggle" className="field-label" style={{ margin: 0 }}>Hide from public listing</label>
          </div>
        </Fieldset>
        <div className="p-field">
          <label htmlFor="date" className="field-label">
            Date
          </label>
          <Calendar
            id="date"
            value={dateValue}
            onChange={handleDateChange}
            showIcon={true}
            dateFormat="yy-mm-dd"
            placeholder="YYYY-MM-DD"
            appendTo={document.body as any}
          />
        </div>

        <div className="p-field">
          <label htmlFor="youtubeId" className="field-label">
            YouTube Video ID
          </label>
          <InputText
            id="youtubeId"
            value={blogPost.youtubeId || ""}
            onChange={(e) => setBlogPost({ ...blogPost, youtubeId: e.target.value })}
            placeholder="e.g. dQw4w9WgXcQ"
          />
          <small aria-live="polite">If provided, the video is embedded full width.</small>
        </div>

        <div className="p-field">
          <label htmlFor="subject" className="field-label">
            Subject
          </label>
          <Dropdown
            id="subject"
            value={blogPost.subject || ""}
            onChange={(e) => setBlogPost({ ...blogPost, subject: String(e.value || "") })}
            options={subjects.map((s) => ({ label: s, value: s }))}
            placeholder="Choose or type a subject"
            showClear
            filter
            editable
            aria-label="Select subject for this post"
            appendTo={document.body as any}
          />
        </div>

        <div className="p-field">
          <label htmlFor="content" className="field-label">
            Content
          </label>
          <Editor id="content" style={{ height: "200px" }} value={blogPost.content} onTextChange={handleRichTextChange} placeholder="Write your blog content here..." />
        </div>

        <Fieldset legend="Voice Transcription">
          <div className="voice-row">
            <div className="voice-actions">
              {!isRecording ? (
                <Button label={transcribing ? "Transcribing..." : "Start Recording"} onClick={startRecording} disabled={transcribing} icon="pi pi-microphone" />
              ) : (
                <Button label="Stop & Transcribe" onClick={stopAndTranscribe} disabled={transcribing} className="p-button-danger" icon="pi pi-pause" />
              )}
              {isRecording && <span className="rec-dot" aria-live="polite" aria-label="Recording" title="Recording" />}
            </div>
            {transcribeError && <div className="ai-error" role="alert">{transcribeError}</div>}
            {lastTranscript && !transcribeError && (
              <div className="voice-preview" aria-live="polite">
                <strong>Last transcript appended:</strong>
                <div className="voice-preview-text">{lastTranscript}</div>
              </div>
            )}
            <div className="voice-hint">Speak in short bursts; each stop transcribes and appends a new paragraph.</div>
          </div>
        </Fieldset>

        <Fieldset legend="AI Image Generator">
          <div className="ai-row">
            <InputText
              id="sdApiKey"
              value={sdApiKey}
              onChange={(e) => {
                setSdApiKey(e.target.value);
                try {
                  localStorage.setItem("stability_api_key", e.target.value);
                } catch {}
              }}
              placeholder="Enter Stability API key (starts with sk-)"
              type="password"
            />
            <Button
              label="Clear Key"
              className="p-button-text p-ml-2"
              onClick={() => {
                setSdApiKey("");
                try {
                  localStorage.removeItem("stability_api_key");
                } catch {}
              }}
            />
          </div>
          <div className="ai-row">
            <InputText id="genPrompt" value={genPrompt} onChange={(e) => setGenPrompt(e.target.value)} placeholder="Describe an image (e.g., Lighthouse on a cliff)" />
            <Button label={genLoading ? "Generating..." : "Generate"} icon="pi pi-image" className="p-button p-ml-2" onClick={generateImage} disabled={genLoading} loading={genLoading} />
          </div>
          {genError && <div className="ai-error">{genError}</div>}
          {genPreviewUrl && (
            <div className="ai-preview">
              <img src={genPreviewUrl} alt="Generated preview" />
              <div className="ai-actions">
                <span>Added to Images</span>
                <Button
                  label="Clear"
                  className="p-button-text p-button-sm"
                  onClick={() => {
                    setGenPreviewUrl(null);
                  }}
                />
              </div>
            </div>
          )}
        </Fieldset>

        <Fieldset legend="Links">
          {blogPost.links &&
            blogPost.links.map((link, index) => (
              <div key={index} className="p-field">
                <label htmlFor={`link${index}`}>Link {index + 1}</label>
                <InputText id={`link${index}`} value={link} onChange={(e) => handleLinkChange(e, index)} placeholder="Enter a URL" />
              </div>
            ))}
          <Button label="Add Link" icon="pi pi-plus" className="p-button-sm" onClick={addLinkField} />
        </Fieldset>
        <Fieldset legend="Images">
          {blogPost.images &&
            blogPost.images.map((image, index) => (
              <div key={index} className="p-field">
                <label htmlFor={`image${index}`}>Image URL {index + 1}</label>
                <InputText id={`image${index}`} value={image} onChange={(e) => handleImageChange(e, index)} placeholder="Enter image URL" />
              </div>
            ))}
          <Button label="Add Image" icon="pi pi-plus" className="p-button-sm" onClick={addImageField} />
        </Fieldset>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Button label={submitting ? "Submitting..." : "Submit Blog Post"} icon="pi pi-check" className="p-button-success p-mt-3" onClick={handleSubmit} disabled={submitting} />
          {submitSuccess && <span className="submit-ok">{submitSuccess}</span>}
          {submitError && <span className="submit-err">{submitError}</span>}
        </div>
      </div>

      <style>{`
              .admin-entry {
                background: #fff;
                border: 3px solid #000;
                box-shadow: 6px 6px 0 #000;
                padding: 12px;
                border-radius: 6px;
              }
              .field-label {
                display: block;
                font-weight: 700;
                margin: 8px 0 6px;
              }
              /* Brutalist inputs/buttons within admin panel */
              .admin-entry .p-inputtext,
              .admin-entry .p-dropdown,
              .admin-entry input[type="text"],
              .admin-entry input[type="password"],
              .admin-entry textarea {
                width: 100% !important;
                border: 3px solid #000 !important;
                box-shadow: 4px 4px 0 #000 !important;
                background: #fff !important;
                padding: 10px 12px !important;
                font-weight: 700;
                border-radius: 4px;
              }

              /* Calendar input and icon button */
              .admin-entry .p-calendar { width: 100%; }
              .admin-entry .p-calendar .p-inputtext { width: 100%; }
              .admin-entry .p-calendar .p-button { 
                border: 3px solid #000;
                box-shadow: 4px 4px 0 #000;
                background: #fff;
                color: #000;
                font-weight: 800;
              }

              /* Buttons */
              .admin-entry .p-button {
                border: 3px solid #000 !important;
                box-shadow: 4px 4px 0 #000 !important;
                background: #fff !important;
                color: #000 !important;
                font-weight: 800 !important;
              }
              .admin-entry .p-button.p-button-success {
                background: #000 !important;
                color: #fff !important;
              }
              .admin-entry .p-button:disabled { opacity: 0.6; }
              .admin-entry .p-button:not(:disabled):hover { transform: translate(-2px, -2px); box-shadow: 6px 6px 0 #000 !important; }

              /* Fieldsets */
              .admin-entry .p-fieldset {
                border: 3px solid #000;
                box-shadow: 6px 6px 0 #000;
                background: #fff;
                margin: 10px 0 14px;
              }
              .admin-entry .p-fieldset .p-fieldset-legend {
                font-weight: 900;
                text-transform: uppercase;
                letter-spacing: .5px;
                border: 3px solid #000;
                background: #fff;
                box-shadow: 4px 4px 0 #000;
              }

              /* Rich text editor */
              .admin-entry .p-editor-container {
                border: 3px solid #000;
                box-shadow: 6px 6px 0 #000;
                border-radius: 6px;
                overflow: hidden;
              }
              .admin-entry .p-editor-toolbar {
                border-bottom: 3px solid #000 !important;
                background: #f3f3f3 !important;
              }
              .admin-entry .p-editor-content {
                min-height: 160px;
                background: #fff;
              }

              .ai-row {
                display: grid;
                grid-template-columns: 1fr auto;
                gap: 8px;
                align-items: center;
                margin-bottom: 8px;
              }
              .ai-error {
                color: #b00020;
                font-size: 0.9rem;
                margin: 4px 0 8px;
              }
              .ai-preview {
                border: 2px dashed #000;
                padding: 8px;
                border-radius: 6px;
                background: #f7f7f7;
              }
              .ai-preview img {
                width: 100%;
                height: auto;
                display: block;
                border-radius: 4px;
                margin-bottom: 8px;
              }
              .ai-actions { display: flex; gap: 8px; }
              /* Voice transcription */
              .voice-row { display: grid; gap: 8px; align-items: center; }
              .voice-actions { display: flex; gap: 8px; align-items: center; }
              .rec-dot { width: 10px; height: 10px; border-radius: 50%; background: #d00; box-shadow: 0 0 0 2px #000; animation: pulse 1s infinite; display: inline-block; }
              @keyframes pulse { 0% { opacity: .4 } 50% { opacity: 1 } 100% { opacity: .4 } }
              .voice-preview { border: 2px dashed #000; background: #f7f7f7; padding: 8px; border-radius: 6px; }
              .voice-preview-text { white-space: pre-wrap; margin-top: 4px; }
              .voice-hint { font-size: .9rem; color: #444; }
              .submit-ok {
                font-weight: 800;
                color: #0a5c0a;
              }
              .submit-err {
                font-weight: 700;
                color: #b00020;
              }
            `}</style>
    </div>
  );
};

export default BlogPostEntry;
